import { X } from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useRef } from 'react'
import { cn } from '../lib/cn'
import { Button } from './Button'

export interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'full'
  /** Kiosk confirmations must not be dismissable by a stray backdrop tap. */
  dismissOnBackdrop?: boolean
  className?: string
}

const SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  full: 'max-w-[92vw]',
} as const

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  dismissOnBackdrop = true,
  className,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null)

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    },
    [onClose],
  )

  useEffect(() => {
    if (!isOpen) return

    document.addEventListener('keydown', handleKeyDown)
    // Prevent the page behind from scrolling while the modal owns the screen.
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    panelRef.current?.focus()

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [isOpen, handleKeyDown])

  if (!isOpen) return null

  return (
    <div role="presentation" className="fixed inset-0 z-40 flex items-center justify-center p-4">
      {/*
        The backdrop is a real button rather than a div with a click handler, so
        dismissing works for keyboard and screen-reader users too — not only for
        a mouse. It is skipped in the tab order because Escape and the header's
        close button are the primary paths.
      */}
      {dismissOnBackdrop ? (
        <button
          type="button"
          tabIndex={-1}
          // Distinct from the header's "Close" so the two do not read as
          // duplicate controls.
          aria-label="Dismiss dialog"
          onClick={onClose}
          className="absolute inset-0 animate-fade-in cursor-default bg-black/80 backdrop-blur-sm"
        />
      ) : (
        <div className="absolute inset-0 animate-fade-in bg-black/80 backdrop-blur-sm" />
      )}

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn(
          'relative w-full animate-slide-up rounded-2xl border border-surface-border bg-surface-overlay shadow-overlay',
          'max-h-[90vh] overflow-y-auto outline-none',
          SIZES[size],
          className,
        )}
      >
        {title && (
          <header className="flex items-start justify-between gap-4 border-surface-border border-b p-5">
            <h2 className="font-semibold text-content text-xl">{title}</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              aria-label="Close"
              className="-mr-2 -mt-1 shrink-0 px-2"
            >
              <X aria-hidden="true" className="size-5" />
            </Button>
          </header>
        )}

        <div className="p-5">{children}</div>

        {footer && (
          <footer className="flex items-center justify-end gap-3 border-surface-border border-t p-5">
            {footer}
          </footer>
        )}
      </div>
    </div>
  )
}
